// src/game/systems/genetics.js
// Pure functions for phenotype rolling — no side effects, fully testable.
import { getStrain } from '../data/strains';

// Rarity probability weights by batch quality score (0–100 integer from calculateHarvestQuality)
const RARITY_WEIGHTS = {
  low:  { common: 75, uncommon: 22, rare: 3  },  // qualityScore 50–64
  mid:  { common: 65, uncommon: 28, rare: 7  },  // qualityScore 65–79
  high: { common: 50, uncommon: 35, rare: 15 },  // qualityScore 80+
};

// Bonus value ranges (decimal multipliers, e.g. 0.12 = +12%)
// Rare primary starts at 0.13 to prevent overlap with Uncommon
const BONUS_RANGES = {
  common:   { primary: [0.08, 0.12], secondary: null },
  uncommon: { primary: [0.10, 0.15], secondary: [0.03, 0.05] },
  rare:     { primary: [0.13, 0.15], secondary: [0.04, 0.06] },
};

// Stat priority pool is determined by the strain's dominant market channel (channelAppeal),
// NOT by strain.genetics. The genetics field alone does not encode market orientation —
// e.g. both bagseed-og (wholesale) and zkittlez (retail) are genetics:'indica'.
// channelAppeal is the authoritative source. Repeated entries = higher probability.
function getStatPool(strain) {
  const ca = strain.channelAppeal;
  if (ca.wholesale >= 0.9)  return ['yield', 'yield', 'thc', 'terpene'];   // NL, bagseed-og
  if (ca.medical   >= 0.9)  return ['thc', 'terpene', 'terpene', 'yield']; // ACDC, cbd-harlequin
  if (ca.retail    >= 1.1)  return ['thc', 'terpene', 'terpene', 'yield']; // wedding-cake, zkittlez, GTH
  return ['yield', 'thc', 'terpene', 'yield'];                              // GG4, default
}

function randRange(min, max) {
  const val = min + Math.random() * (max - min);
  return Math.round(val * 100) / 100;
}

function pickRarity(qualityScore, hasSequencer) {
  const tier = qualityScore >= 80 ? 'high' : qualityScore >= 65 ? 'mid' : 'low';
  const w = RARITY_WEIGHTS[tier];
  const rareAdj = hasSequencer ? 3 : 0;
  const rare     = w.rare + rareAdj;
  const uncommon = w.uncommon;
  const roll = Math.random() * 100;
  if (roll < rare) return 'rare';
  if (roll < rare + uncommon) return 'uncommon';
  return 'common';
}

function rollCandidate(strain, qualityScore, hasSequencer) {
  const rarity = pickRarity(qualityScore, hasSequencer);
  const ranges = BONUS_RANGES[rarity];

  // Pick primary stat, weighted by strain's channel appeal
  const pool = getStatPool(strain);
  const primaryStat = pool[Math.floor(Math.random() * pool.length)];
  const primaryBonus = randRange(ranges.primary[0], ranges.primary[1]);

  const bonuses = { [primaryStat]: primaryBonus };

  if (ranges.secondary) {
    const secondaryOptions = ['yield', 'thc', 'terpene'].filter(s => s !== primaryStat);
    const secondaryStat = secondaryOptions[Math.floor(Math.random() * secondaryOptions.length)];
    bonuses[secondaryStat] = randRange(ranges.secondary[0], ranges.secondary[1]);
  }

  return { rarity, bonuses };
}

/**
 * Roll 2–3 phenotype candidates for a harvest.
 * @param {string} strainId
 * @param {number} qualityScore  integer 0–100 from calculateHarvestQuality
 * @param {boolean} hasSequencer  Gene Sequencer installed in lab → 3rd candidate + better rarity
 * @returns {Array<{ rarity: string, bonuses: Object }>}
 */
export function rollPhenoCandidates(strainId, qualityScore, hasSequencer) {
  const strain = getStrain(strainId);
  if (!strain) {
    console.warn(`[genetics] rollPhenoCandidates: unknown strainId "${strainId}"`);
    return [];
  }
  const count = hasSequencer ? 3 : 2;
  return Array.from({ length: count }, () => rollCandidate(strain, qualityScore, hasSequencer));
}
